import { Box } from "@mui/material";
import { Provider } from "@/services";
import { useState } from "react";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import SuccessModal, {
  useSuccess,
} from "../../../../../components/SuccessModal";
import ToggleSwitch from "../../../../../components/ToggleSwitch";
import ActionMenu from "../../../../../components/app/ActionMenu";
import { STATE_LIST } from "../../../../../constants";
import { ProviderUserType } from "../../../../../constants/ProviderUserSchema";
import { UserData } from "../../../../../dummy-data/user-data";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useStatusUsers } from "../../../../../queries/use-status-users";
import { useUpdateProviderUserStatus } from "../../../../../queries/use-update-provider-status";
import { useUpdateProviderUser } from "../../../../../queries/use-update-provider-user";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  transformMultiSelectFrontEndVal,
  getUnNecessaryTransformData,
  DEFAULT_VALUE_LABEL_LIST,
  DEFAULT_UUID_NAME_LIST,
  getXtenantId,
} from "../../../../../utils/utils";
import Text from "../../../../../components/Text";
import { useQueryClient } from "@tanstack/react-query";
import UpsertProviderUserDrawer from "../../../../../components/app/admin/provider-groups/ProviderUserDrawer";
import { GridSortItem } from "@mui/x-data-grid";
import { useResendLink } from "../../../../../queries/use-resend-link";
import { useOutletContext } from "react-router-dom";
import DeleteModal from "../../../../../components/app/DeleteModal";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { useArchiveReferralProvider } from "../../../../../queries/collaboration/referral/use-referral-provider-archive";
import { useProvider } from "../../../../../queries/use-provider";
import useCurrentUser, {
  CurrentUser,
} from "../../../../../hooks/local-storage/use-current-user";
import UserEmailCell from "../../../../../components/UserEmailCell";
import {
  RESEND_LINK_DISABLED_MSG,
  RESEND_LINK_LABEL,
  USER_TABLE_ROW_HEIGHT,
} from "./user-setting-constant";
import { UserCardData } from "../../../../../types/user-types";

const ProviderUserTable = ({
  userList,
  searchKey,
  gridSorting,
  setGridSorting,
  refetch,
}: {
  userList?: UserCardData[];
  searchKey: string;
  gridSorting: GridSortItem[];
  setGridSorting: (gridSorting: GridSortItem[]) => void;
  refetch: () => void;
}) => {
  const { mutateAsync: statusChange } = useStatusUsers();
  const { profileData }: any = useOutletContext();
  const {
    opened: openedProviderUserModal,
    open: openProviderUserModal,
    close: closeProviderUserModal,
  } = useDisclosure(false);
  const [openedResendLink, { open: openResendLink, close: closeResendLink }] =
    useSuccess();
  const { mutateAsync: updateProviderUser } = useUpdateProviderUser();
  const [archive, setArchive] = useState(false);
  const { mutateAsync: archiveProvider } = useArchiveReferralProvider();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveProvider);
  const { mutateAsync: providerStatusUpdate } = useUpdateProviderUserStatus();
  const queryClient = useQueryClient();
  const [me, setMe] = useCurrentUser();

  function editProviderUser(values: ProviderUserType) {
    // The form holds display shapes the generated models cannot describe —
    // multi-select option lists, a string npi, collections as arrays rather
    // than the generated `Set` — so the payload is converted once here.
    const payload = getUnNecessaryTransformData(
      ["acceptedInsurances", "licensedStates", "workLocations", "specialities"],
      values
    );
    return updateProviderUser({
      providerData: payload as unknown as Provider,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      if (values?.uuid === me?.uuid) {
        setMe(payload as unknown as CurrentUser);
      }
      return res;
    });
  }

  const [selectedProviderUserId, setSelectedProviderUserId] =
    useState<string>();
  const { data: providerDetail } = useProvider({
    providerUuid: selectedProviderUserId,
    tenantId: getXtenantId(),
  });
  const { mutateAsync: resendInvitationLink } = useResendLink();

  function handleOpenEditModal(providerData: ProviderUserType) {
    setSelectedProviderUserId(providerData?.uuid);
    openProviderUserModal();
  }

  const handleSendInviteLink = ({ email }: { email: string }) => {
    resendInvitationLink({
      linkType: "set",
      setPasswordRequest: {
        email: email,
      },
      xTENANTID: getXtenantId(),
    }).then(() => openResendLink());
  };

  function toggleStatus<T>(value: boolean, id: T, role: string) {
    if (role === "PROVIDER") {
      return providerStatusUpdate({
        providerId: id?.toString() ?? "",
        status: value,
        xTENANTID: getXtenantId(),
      }).then((res) => {
        queryClient.invalidateQueries({
          queryKey: ["provider-group-users-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["providers-List"],
        });
        return res;
      });
    }

    return statusChange({
      userId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      queryClient.invalidateQueries({
        queryKey: ["provider-group-users-list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["providers-List"],
      });
      return res;
    });
  }

  const COLUMNS = applyDefaultColumnSettings<UserData>(
    [
      // {
      //   field: "uuid",
      //   flex: 0.4,
      //   minWidth: 80,
      //   headerName: "User ID",
      //   sortable: false,
      //   renderCell: ({ row }) => {
      //     return (
      //       <Text>{(row as any)?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
      //     );
      //   },
      // },
      {
        field: "providerName",
        flex: 0.9,
        minWidth: 200,
        disableColumnMenu: true,
        headerName: "Name",
        // todo need to fix type
        renderCell: ({ row }: any) => {
          return (
            <Box

            // onClick={() => {
            //   row.roleType === "STAFF"
            //     ? navigate(`/app/provider-groups/users/staff/${row.uuid}`)
            //     : navigate(`/app/provider-groups/users/provider/${row.uuid}`);
            // }}
            >
              <Text isTabletext>{`${row?.firstName} ${row.lastName}`}</Text>
              <Text isTabletext variant="subtitle2">
                {row?.age}
              </Text>
            </Box>
          );
        },
        sortable: false,
      },
      {
        field: "role",
        flex: 0.8,
        minWidth: 130,
        headerName: "Role",
        sortable: false,
      },
      {
        field: "phone",
        flex: 0.6,
        minWidth: 140,
        headerName: "Contact",
        sortable: false,
      },
      {
        field: "email",
        flex: 0.9,
        minWidth: 240,
        headerName: "Email",
        renderCell: ({ row, value }) => (
          <UserEmailCell
            email={value}
            temporaryEmail={row?.temporaryEmail}
            isVerified={row?.emailVerified}
          />
        ),
        sortable: false,
      },
      {
        field: "lastLogin",
        flex: 0.7,
        minWidth: 150,
        headerName: "Last Sign In",
        sortable: false,
        renderCell: ({ ...params }) => {
          return (
            <Text isTabletext>
              {params.value ? formatDateTime(params.value) : "-"}
            </Text>
          );
        },
      },
      {
        field: "active",
        width: 150,
        headerName: "Status",
        renderCell: ({ row, value }) => (
          <ToggleSwitch
            id={row.uuid.toString()}
            checked={value}
            onConfirm={(v, id) => toggleStatus(v, id, row?.roleType)}
          />
        ),
        sortable: false,
      },
      {
        field: "Action",
        width: 90,
        // todo need to fix type
        renderCell: ({ row }: any) => {
          return (
            <ActionMenu
              options={[
                {
                  value: "resendLink",
                  label: RESEND_LINK_LABEL,
                  isDisabled: Boolean(row?.emailVerified),
                  hoverMsg: RESEND_LINK_DISABLED_MSG.PROVIDER_VERIFIED,
                },
                { value: "edit", label: "Edit" },
                {
                  value: "archive",
                  label: row?.archive ? "Unarchive" : "Archive",
                  isDisabled: row?.active,
                  hoverMsg: "Make status disable for archive",
                },
              ]}
              onActionClick={(v) => {
                if (v === "edit") {
                  handleOpenEditModal(row);
                } else if (v === "archive") {
                  setArchive(row?.archive);
                  deleteDisc.open(row?.uuid);
                } else
                  handleSendInviteLink({
                    email:
                      !row?.emailVerified && row?.temporaryEmail
                        ? row.temporaryEmail
                        : row?.email,
                  });
              }}
            />
          );
        },
      },
    ],
    ["lastLogin", "providerName"]
  );

  return (
    <Box height={"100%"} width={"100%"}>
      <CustomDataGridV2
        rowHeight={USER_TABLE_ROW_HEIGHT}
        searchString={searchKey}
        getRowId={(row) => row.uuid}
        rows={userList ?? []}
        columns={COLUMNS}
        onSortModelChange={setGridSorting}
        disableRowSelectionOnClick
        hideFooter={true}
        sortModel={gridSorting}
      />

      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archive, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          archive ? "unarchive" : "archive"
        } this Provider User`}
      />

      <SuccessModal
        label="The invitation link has been sent successfully to your email."
        opened={openedResendLink}
        onClose={closeResendLink}
      />

      <UpsertProviderUserDrawer
        shouldGetPrefillList
        open={openedProviderUserModal}
        prefillSpecialtyList={transformMultiSelectFrontEndVal(
          DEFAULT_UUID_NAME_LIST,
          profileData?.specialities
        )?.map((it) => ({ uuid: it?.uuid, name: it?.name }))}
        title={"Edit Provider User"}
        onClose={() => {
          closeProviderUserModal();
          setSelectedProviderUserId(undefined);
        }}
        onSubmit={editProviderUser}
        prefillValues={{
          ...providerDetail?.data,
          licensedStates: transformMultiSelectFrontEndVal(
            STATE_LIST,
            providerDetail?.data?.licensedStates
          ),
          ageGroupSeen: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerDetail?.data?.ageGroupSeen
          ),
          careService: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerDetail?.data?.careService
          ),
          specialities: transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            providerDetail?.data?.specialities
          ),

          providerType: providerDetail?.data?.providerType ?? undefined,
          // groupNpiNumber: groupNpi,
          acceptedInsurances: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerDetail?.data?.acceptedInsurances
          ),
          workLocations: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerDetail?.data?.workLocations
          ),
          spokenLanguages: transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            providerDetail?.data?.spokenLanguages
          ),
        }}
      />
    </Box>
  );
};

export default ProviderUserTable;
